import { useState, useMemo, useEffect } from "react";
import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Checkbox,
  Chip,
  Spinner,
  addToast,
} from "@heroui/react";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import dayjs from "dayjs";
import { api } from "@/lib/api-client";
import { GetCustomerSubscription } from "@/server-types";
import { getLocalizedName } from "@/utils";
import { i18n } from "@lingui/core";

type ValidationStatus =
  | { status: "valid" }
  | { status: "already_linked"; reason: string }
  | { status: "service_mismatch"; reason: string }
  | { status: "outside_validity_period"; reason: string }
  | { status: "in_freeze_period"; reason: string };

type CustomerSubscriptionLinkAppointmentsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  customerSubscription: GetCustomerSubscription["response"]["customerSubscription"];
};

export const CustomerSubscriptionLinkAppointmentsModal = ({
  isOpen,
  onClose,
  customerSubscription,
}: CustomerSubscriptionLinkAppointmentsModalProps) => {
  const { mutateAsync: linkAppointments, isPending: isLinking } =
    api.customer.usePostCustomerSubscriptionLinkAppointments();

  const {
    data: customerAppointmentsData,
    isFetching: isLoadingAppointments,
    processedErrorMessage,
  } = api.customer.useGetCustomerAppointments({
    customerId: customerSubscription.customerId,
    query: {
      status: "all",
      pagination: {
        page: 1,
        perPage: 500,
      },
    },
  });

  const { locale } = i18n;

  const appointmentsWithValidation = useMemo(() => {
    if (!customerAppointmentsData?.customerAppointments) return [];

    const subscriptionServiceIds = new Set(
      customerSubscription.customerSubscriptionServices.map((s) => s.serviceId),
    );

    const nonDeletedAppointments =
      customerAppointmentsData.customerAppointments.filter(
        (ua) => !ua.isDeleted && !ua.appointment.isDeleted,
      );

    return nonDeletedAppointments.map((ua) => ({
      ...ua,
      validationStatus: validateAppointment(
        ua,
        customerSubscription.id,
        subscriptionServiceIds,
        customerSubscription,
      ),
    }));
  }, [customerAppointmentsData?.customerAppointments, customerSubscription]);

  const initialSelectedIds = useMemo(
    () =>
      new Set(
        appointmentsWithValidation.flatMap((a) =>
          a.customerSubscriptionId === customerSubscription.id
            ? [a.userAppointmentId]
            : [],
        ),
      ),
    [appointmentsWithValidation, customerSubscription.id],
  );

  const [selectedAppointmentIds, setSelectedAppointmentIds] =
    useState<Set<number>>(initialSelectedIds);

  useEffect(() => {
    setSelectedAppointmentIds(initialSelectedIds);
  }, [initialSelectedIds]);

  const appointmentsToProcess = useMemo(() => {
    return appointmentsWithValidation.filter((a) => {
      const isSelected = selectedAppointmentIds.has(a.userAppointmentId);
      const isLinked = a.customerSubscriptionId === customerSubscription.id;
      return isSelected !== isLinked;
    });
  }, [
    appointmentsWithValidation,
    selectedAppointmentIds,
    customerSubscription.id,
  ]);

  const groupedAppointments = useMemo(() => {
    const groups = new Map<string, AppointmentWithValidation[]>();
    for (const appointment of appointmentsWithValidation) {
      const monthKey = dayjs(appointment.appointment.startTimeUtc).format(
        "YYYY-MM",
      );
      if (!groups.has(monthKey)) {
        groups.set(monthKey, []);
      }
      groups.get(monthKey)!.push(appointment);
    }
    return groups;
  }, [appointmentsWithValidation]);

  const matchingServiceUnlinkedAppointmentsCount = useMemo(
    () =>
      appointmentsWithValidation.filter((appointment) => {
        const isMatchingService =
          appointment.validationStatus.status !== "service_mismatch";
        const isSelected = selectedAppointmentIds.has(
          appointment.userAppointmentId,
        );

        return isMatchingService && !isSelected;
      }).length,
    [appointmentsWithValidation, selectedAppointmentIds],
  );

  const areAllLinkableAppointmentsSelected = useMemo(() => {
    const linkableAppointmentIds = appointmentsWithValidation.flatMap(
      (appointment) =>
        appointment.validationStatus.status === "valid" ||
        appointment.customerSubscriptionId === customerSubscription.id
          ? [appointment.userAppointmentId]
          : [],
    );

    if (linkableAppointmentIds.length === 0) return false;

    return linkableAppointmentIds.every((id) => selectedAppointmentIds.has(id));
  }, [
    appointmentsWithValidation,
    customerSubscription.id,
    selectedAppointmentIds,
  ]);

  const handleToggleAppointment = (userAppointmentId: number) => {
    setSelectedAppointmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(userAppointmentId)) {
        next.delete(userAppointmentId);
      } else {
        next.add(userAppointmentId);
      }
      return next;
    });
  };

  const handleLinkAllAppointments = () => {
    const nextSelection = new Set<number>();

    for (const appointment of appointmentsWithValidation) {
      if (
        appointment.validationStatus.status === "valid" ||
        appointment.customerSubscriptionId === customerSubscription.id
      ) {
        nextSelection.add(appointment.userAppointmentId);
      }
    }

    setSelectedAppointmentIds(nextSelection);
  };

  const handleLink = async () => {
    if (appointmentsToProcess.length === 0) return;

    try {
      const { appointmentsToLink, appointmentsToUnlink } =
        appointmentsToProcess.reduce<{
          appointmentsToLink: number[];
          appointmentsToUnlink: number[];
        }>(
          (acc, appointment) => {
            const isSelected = selectedAppointmentIds.has(
              appointment.userAppointmentId,
            );
            if (isSelected) {
              acc.appointmentsToLink.push(appointment.userAppointmentId);
            } else {
              acc.appointmentsToUnlink.push(appointment.userAppointmentId);
            }
            return acc;
          },
          { appointmentsToLink: [], appointmentsToUnlink: [] },
        );

      await linkAppointments({
        customerSubscriptionId: customerSubscription.id,
        body: {
          appointmentsToLink,
          appointmentsToUnlink,
        },
      });

      addToast({
        title: t`Termini posodobljeni`,
        color: "success",
      });

      setSelectedAppointmentIds(new Set());
      onClose();
    } catch (error) {
      console.error(error);
      addToast({
        title: t`Napaka`,
        description: t`Napaka pri posodabljanju terminov`,
        color: "danger",
      });
    }
  };

  return (
    <LimeModal
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="5xl"
    >
      <ModalContent>
        <ModalHeader>
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-semibold">
              <Trans>Upravljaj povezave terminov s paketom</Trans>
            </h2>
            <p className="text-default-500 text-sm">
              <Trans>
                Izberite termine, ki jih želite povezati ali odvezati od paketa
              </Trans>
            </p>
          </div>
        </ModalHeader>
        <ModalBody>
          {isLoadingAppointments ? (
            <div className="flex w-full justify-center py-8">
              <Spinner />
            </div>
          ) : appointmentsWithValidation.length === 0 ? (
            <div className="text-default-500 py-8 text-center">
              <Trans>Ni na voljo terminov za povezovanje</Trans>
            </div>
          ) : (
            <div className="max-h-96 space-y-4 overflow-y-auto">
              {Array.from(groupedAppointments.entries()).map(
                ([monthKey, appointments]) => (
                  <div key={monthKey} className="space-y-2">
                    <h4 className="text-default-600 text-sm font-medium">
                      {dayjs(monthKey).toDate().toLocaleDateString(locale, {
                        month: "long",
                        year: "numeric",
                      })}
                    </h4>
                    <div className="space-y-1">
                      {appointments.map((appointment) => {
                        const isSelected = selectedAppointmentIds.has(
                          appointment.userAppointmentId,
                        );
                        const validationReason =
                          appointment.validationStatus.status !== "valid"
                            ? appointment.validationStatus.status ===
                                "already_linked" && !isSelected
                              ? null
                              : appointment.validationStatus.reason
                            : null;
                        const appointmentDate = dayjs(
                          appointment.appointment.startTimeUtc,
                        );
                        const validFromDate = dayjs(
                          customerSubscription.validFrom,
                        );
                        const validToDate = dayjs(customerSubscription.validTo);
                        const isWithinValidityPeriod =
                          appointmentDate.isBetween(
                            validFromDate,
                            validToDate,
                            null,
                            "[]",
                          );
                        const serviceName = getLocalizedName(
                          appointment.appointment.service.nameLocalized,
                        );
                        const instructorName = extractInstructorNames(
                          appointment.appointment.appointmentUsers,
                        );

                        return (
                          <div
                            key={appointment.userAppointmentId}
                            className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
                              isSelected
                                ? "border-primary bg-primary-50"
                                : "border-default-200 hover:bg-default-50"
                            }`}
                          >
                            <Checkbox
                              isSelected={isSelected}
                              onValueChange={() =>
                                handleToggleAppointment(
                                  appointment.userAppointmentId,
                                )
                              }
                              color="primary"
                            />
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium">
                                  {
                                    appointment.appointment
                                      .startTimeLocalFormatted
                                  }
                                </p>
                                <Chip
                                  size="sm"
                                  color={
                                    isWithinValidityPeriod
                                      ? "success"
                                      : "warning"
                                  }
                                  variant="flat"
                                >
                                  {isWithinValidityPeriod ? (
                                    <Trans>V trenutnem obdobju paketa</Trans>
                                  ) : (
                                    <Trans>
                                      Izven trenutnega obdobja paketa
                                    </Trans>
                                  )}
                                </Chip>
                              </div>
                              <p className="text-default-500 text-sm">
                                {serviceName}
                                {instructorName && ` • ${instructorName}`}
                              </p>
                              {validationReason && (
                                <p className="text-danger mt-1 text-xs">
                                  {validationReason}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ),
              )}
            </div>
          )}

          <div className="sticky bottom-0 flex flex-col gap-2">
            <p className="text-sm font-medium">
              <Trans>Izbrani termini: {selectedAppointmentIds.size}</Trans>
            </p>
            {matchingServiceUnlinkedAppointmentsCount > 0 && (
              <p className="text-default-500 text-xs">
                <Trans>
                  Število terminov, ki niso vključeni v paket, a vsebujejo
                  storitev, vključeno v paket:{" "}
                  {matchingServiceUnlinkedAppointmentsCount}
                </Trans>
              </p>
            )}
            <Button
              color="primary"
              variant={areAllLinkableAppointmentsSelected ? "flat" : "bordered"}
              size="sm"
              className="w-fit"
              onPress={handleLinkAllAppointments}
              isDisabled={matchingServiceUnlinkedAppointmentsCount === 0}
            >
              <Trans>Poveži vse veljavne termine</Trans>
            </Button>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex w-full flex-col gap-3">
            {processedErrorMessage && (
              <LimeAlert color="danger" message={processedErrorMessage} />
            )}
            <div className="flex justify-end gap-2">
              <Button variant="light" onPress={onClose} isDisabled={isLinking}>
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                color="primary"
                variant="solid"
                onPress={handleLink}
                isLoading={isLinking}
                isDisabled={appointmentsToProcess.length === 0}
              >
                <Trans>Shrani</Trans>
              </Button>
            </div>
          </div>
        </ModalFooter>
      </ModalContent>
    </LimeModal>
  );
};

type AppointmentWithValidation = NonNullable<
  ReturnType<typeof api.customer.useGetCustomerAppointments>["data"]
>["customerAppointments"][number] & {
  validationStatus: ValidationStatus;
};

const validateAppointment = (
  userAppointment: {
    customerSubscriptionId: number | null;
    appointment: {
      startTimeUtc: Date;
      service: { serviceId: number | null };
    };
  },
  customerSubscriptionId: number,
  subscriptionServiceIds: Set<number>,
  subscription: CustomerSubscriptionLinkAppointmentsModalProps["customerSubscription"],
): ValidationStatus => {
  if (userAppointment.customerSubscriptionId === customerSubscriptionId) {
    return {
      status: "already_linked",
      reason: t`Termin je že povezan s paketom`,
    };
  }

  if (
    !userAppointment.appointment.service.serviceId ||
    !subscriptionServiceIds.has(userAppointment.appointment.service.serviceId)
  ) {
    return {
      status: "service_mismatch",
      reason: t`Storitev termina se ne ujema s storitvami paketa`,
    };
  }

  const appointmentDate = dayjs(userAppointment.appointment.startTimeUtc);
  const validFrom = dayjs(subscription.validFrom);
  const validTo = dayjs(subscription.validTo);

  if (
    !subscription.isRecurring &&
    (appointmentDate.isBefore(validFrom) || appointmentDate.isAfter(validTo))
  ) {
    return {
      status: "outside_validity_period",
      reason: t`Termin je zunaj veljavnega obdobja paketa`,
    };
  }

  const isInFreeze = subscription.ScheduledSubscriptionFreeze?.some((freeze) =>
    appointmentDate.isBetween(
      dayjs(freeze.freezeStart),
      dayjs(freeze.freezeEnd),
      null,
      "[]",
    ),
  );

  if (isInFreeze) {
    return {
      status: "in_freeze_period",
      reason: t`Termin je v obdobju zamrznitve paketa`,
    };
  }

  return { status: "valid" };
};

const extractInstructorNames = (
  appointmentUsers?: Array<{
    user?: { name?: string | null; lastName?: string | null } | null;
  }>,
): string => {
  if (!appointmentUsers?.length) return "";

  return appointmentUsers
    .flatMap((au) => {
      const parts = [au.user?.name, au.user?.lastName].filter(
        (part) => part && part.length > 0,
      );
      const name = parts.join(" ").trim();
      return name.length > 0 ? [name] : [];
    })
    .join(", ");
};
